import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/* ═══════════════════════════════════════════════════════════════════════════
   QA Testing & Health Monitor — Admin Panel
   ═══════════════════════════════════════════════════════════════════════════ */

const SEVERITY_COLORS = {
  critical: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', badge: '#dc2626' },
  high:     { bg: '#fff7ed', border: '#fdba74', text: '#9a3412', badge: '#ea580c' },
  medium:   { bg: '#fefce8', border: '#fde047', text: '#854d0e', badge: '#ca8a04' },
  low:      { bg: '#f0fdf4', border: '#86efac', text: '#166534', badge: '#16a34a' },
  pass:     { bg: '#f0fdf4', border: '#86efac', text: '#166534', badge: '#22c55e' },
};

const STATUS_ICON = { pass: '\u2705', fail: '\u274C', warn: '\u26A0\uFE0F', running: '\u23F3', pending: '\u23F8\uFE0F' };

function Badge({ severity }) {
  const c = SEVERITY_COLORS[severity] || SEVERITY_COLORS.medium;
  return (
    <span style={{
      background: c.badge, color: '#fff', padding: '2px 10px',
      borderRadius: 99, fontSize: 10, fontWeight: 800,
      textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>{severity}</span>
  );
}

function ResultCard({ title, status, severity, details, fix, timestamp }) {
  const [expanded, setExpanded] = useState(false);
  const c = SEVERITY_COLORS[severity] || SEVERITY_COLORS.medium;
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: 12, padding: '14px 18px', marginBottom: 10,
      cursor: details || fix ? 'pointer' : 'default',
    }} onClick={() => setExpanded(!expanded)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 16 }}>{STATUS_ICON[status] || STATUS_ICON.pending}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: c.text }}>{title}</div>
          {timestamp && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{timestamp}</div>}
        </div>
        <Badge severity={severity} />
        {(details || fix) && (
          <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>{expanded ? '\u25B2' : '\u25BC'}</span>
        )}
      </div>
      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${c.border}` }}>
          {details && <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: fix ? 10 : 0 }}>{details}</div>}
          {fix && (
            <div style={{
              background: '#0f172a', color: '#e2e8f0', padding: '10px 14px',
              borderRadius: 8, fontSize: 11, fontFamily: 'monospace',
              whiteSpace: 'pre-wrap', lineHeight: 1.5,
            }}>{fix}</div>
          )}
        </div>
      )}
    </div>
  );
}

function ProgressBar({ progress, label }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{label}</span>
        <span style={{ fontSize: 12, color: '#64748b' }}>{progress}%</span>
      </div>
      <div style={{ background: '#e2e8f0', borderRadius: 99, height: 6, overflow: 'hidden' }}>
        <div style={{
          width: `${progress}%`, height: '100%',
          background: progress === 100
            ? 'linear-gradient(90deg, #22c55e, #16a34a)'
            : 'linear-gradient(90deg, #3b82f6, #6366f1)',
          borderRadius: 99, transition: 'width 0.5s ease',
        }} />
      </div>
    </div>
  );
}

/* ── Test Definitions ─────────────────────────────────────────────────────── */

function getTests(supabase) {
  return [
    // ─── Database Health ─────────────────────────────────────────────────
    {
      id: 'db-tables',
      category: 'Database Health',
      name: 'Required tables exist',
      run: async () => {
        const tables = ['customers', 'kids', 'settings', 'devices', 'app_usage', 'websites', 'alerts', 'screenshots', 'issues'];
        const missing = [];
        for (const t of tables) {
          const { error } = await supabase.from(t).select('id').limit(1);
          if (error) missing.push(t);
        }
        if (missing.length > 0) return {
          status: 'fail', severity: 'critical',
          details: `Missing tables: ${missing.join(', ')}`,
        };
        return { status: 'pass', severity: 'pass', details: `All ${tables.length} tables accessible` };
      },
    },
    {
      id: 'db-customers-integrity',
      category: 'Database Health',
      name: 'Customer data integrity',
      run: async () => {
        const { data: custs } = await supabase.from('customers').select('id, auth_id, name, email, plan');
        const issues = [];
        (custs || []).forEach(c => {
          if (!c.auth_id) issues.push(`${c.name || c.id}: missing auth_id`);
          if (!c.email) issues.push(`${c.name || c.id}: missing email`);
          if (!['free', 'trial', 'plus', 'family'].includes(c.plan)) issues.push(`${c.name || c.id}: invalid plan "${c.plan}"`);
        });
        if (issues.length > 0) return {
          status: 'warn', severity: 'medium',
          details: `${issues.length} issue(s) found:\n${issues.slice(0, 10).join('\n')}${issues.length > 10 ? `\n... and ${issues.length - 10} more` : ''}`,
        };
        return { status: 'pass', severity: 'pass', details: `${(custs || []).length} customers — all valid` };
      },
    },
    {
      id: 'db-orphan-kids',
      category: 'Database Health',
      name: 'No orphaned kids (missing parent)',
      run: async () => {
        const { data: allKids } = await supabase.from('kids').select('id, kid_name, customer_id');
        const { data: custs } = await supabase.from('customers').select('id');
        const custIds = new Set((custs || []).map(c => c.id));
        const orphans = (allKids || []).filter(k => !custIds.has(k.customer_id));
        if (orphans.length > 0) return {
          status: 'fail', severity: 'high',
          details: `${orphans.length} orphaned kid(s): ${orphans.map(k => k.kid_name || k.id).join(', ')}`,
          fix: `-- Delete orphaned kids:\nDELETE FROM kids WHERE customer_id NOT IN (SELECT id FROM customers);`,
        };
        return { status: 'pass', severity: 'pass', details: `${(allKids || []).length} kids — all linked` };
      },
    },
    {
      id: 'db-orphan-settings',
      category: 'Database Health',
      name: 'No orphaned settings (missing user)',
      run: async () => {
        const { data: settings } = await supabase.from('settings').select('id, parent_id');
        const { data: custs } = await supabase.from('customers').select('auth_id');
        const authIds = new Set((custs || []).map(c => c.auth_id));
        const orphans = (settings || []).filter(s => !authIds.has(s.parent_id));
        if (orphans.length > 0) return {
          status: 'warn', severity: 'low',
          details: `${orphans.length} settings row(s) with no matching customer auth_id`,
        };
        return { status: 'pass', severity: 'pass', details: `${(settings || []).length} settings — all linked` };
      },
    },
    {
      id: 'db-duplicate-emails',
      category: 'Database Health',
      name: 'No duplicate customer emails',
      run: async () => {
        const { data: custs } = await supabase.from('customers').select('id, name, email');
        const counts = {};
        (custs || []).forEach(c => {
          if (c.email) {
            const e = c.email.trim().toLowerCase();
            if (!counts[e]) counts[e] = [];
            counts[e].push(c.name || c.id);
          }
        });
        const dupes = Object.entries(counts).filter(([, names]) => names.length > 1);
        if (dupes.length > 0) return {
          status: 'fail', severity: 'critical',
          details: `Duplicate email(s) found:\n${dupes.map(([email, names]) => `  ${email}: ${names.join(', ')}`).join('\n')}`,
          fix: `-- Keep the newest account, delete older duplicates:\n-- Review manually in Admin > Customers before deleting`,
        };
        return { status: 'pass', severity: 'pass', details: `${(custs || []).length} customers — all unique emails` };
      },
    },
    {
      id: 'db-duplicate-phones',
      category: 'Database Health',
      name: 'No duplicate customer phones',
      run: async () => {
        const { data: custs } = await supabase.from('customers').select('id, name, phone');
        const counts = {};
        (custs || []).forEach(c => {
          if (c.phone && c.phone.trim()) {
            const p = c.phone.trim().replace(/\s+/g, '');
            if (!counts[p]) counts[p] = [];
            counts[p].push(c.name || c.id);
          }
        });
        const dupes = Object.entries(counts).filter(([, names]) => names.length > 1);
        if (dupes.length > 0) return {
          status: 'fail', severity: 'high',
          details: `Duplicate phone(s) found:\n${dupes.map(([phone, names]) => `  ${phone}: ${names.join(', ')}`).join('\n')}`,
          fix: `-- Review and merge or delete duplicate accounts in Admin > Customers`,
        };
        return { status: 'pass', severity: 'pass', details: 'All phone numbers unique' };
      },
    },
    {
      id: 'db-duplicate-usernames',
      category: 'Database Health',
      name: 'No duplicate kid usernames',
      run: async () => {
        const { data: allKids } = await supabase.from('kids').select('kid_username');
        const counts = {};
        (allKids || []).forEach(k => {
          if (k.kid_username) counts[k.kid_username] = (counts[k.kid_username] || 0) + 1;
        });
        const dupes = Object.entries(counts).filter(([, c]) => c > 1);
        if (dupes.length > 0) return {
          status: 'fail', severity: 'high',
          details: `Duplicate usernames: ${dupes.map(([u, c]) => `@${u} (${c}x)`).join(', ')}`,
        };
        return { status: 'pass', severity: 'pass', details: 'All usernames unique' };
      },
    },

    // ─── Trial & Plan Checks ────────────────────────────────────────────
    {
      id: 'trial-expired',
      category: 'Business Logic',
      name: 'Expired trials still on trial plan',
      run: async () => {
        const { data: trials } = await supabase.from('customers')
          .select('id, name, email, plan, trial_ends_at')
          .eq('plan', 'trial');
        const now = new Date();
        const expired = (trials || []).filter(t => t.trial_ends_at && new Date(t.trial_ends_at) < now);
        if (expired.length > 0) return {
          status: 'fail', severity: 'high',
          details: `${expired.length} expired trial(s) still on "trial" plan:\n${expired.map(t => `  ${t.name} (${t.email}) — expired ${t.trial_ends_at}`).join('\n')}`,
          fix: `-- Fix: UPDATE customers SET plan = 'free' WHERE plan = 'trial' AND trial_ends_at < NOW();`,
        };
        return { status: 'pass', severity: 'pass', details: `${(trials || []).length} active trial(s) — all valid` };
      },
    },
    {
      id: 'settings-missing',
      category: 'Business Logic',
      name: 'All customers have settings row',
      run: async () => {
        const { data: custs } = await supabase.from('customers').select('id, auth_id, name');
        const { data: settings } = await supabase.from('settings').select('parent_id');
        const settingsSet = new Set((settings || []).map(s => s.parent_id));
        const missing = (custs || []).filter(c => c.auth_id && !settingsSet.has(c.auth_id));
        if (missing.length > 0) return {
          status: 'warn', severity: 'medium',
          details: `${missing.length} customer(s) without settings: ${missing.map(c => c.name || c.id).join(', ')}`,
        };
        return { status: 'pass', severity: 'pass', details: 'All customers have settings' };
      },
    },

    // ─── Device & Connectivity ──────────────────────────────────────────
    {
      id: 'devices-stale',
      category: 'Devices & Connectivity',
      name: 'Stale devices (offline > 7 days)',
      run: async () => {
        const { data: devs } = await supabase.from('devices').select('id, device_name, parent_id, last_seen_at');
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const stale = (devs || []).filter(d =>
          d.device_name && d.last_seen_at && new Date(d.last_seen_at) < sevenDaysAgo
        );
        if (stale.length > 0) return {
          status: 'warn', severity: 'low',
          details: `${stale.length} device(s) offline > 7 days: ${stale.map(d => d.device_name).join(', ')}`,
        };
        return { status: 'pass', severity: 'pass', details: `All ${(devs || []).filter(d => d.device_name).length} devices recently active` };
      },
    },
    {
      id: 'devices-no-parent',
      category: 'Devices & Connectivity',
      name: 'All devices have a parent_id',
      run: async () => {
        const { data: devs } = await supabase.from('devices').select('id, device_name, parent_id');
        const orphans = (devs || []).filter(d => d.device_name && !d.parent_id);
        if (orphans.length > 0) return {
          status: 'fail', severity: 'medium',
          details: `${orphans.length} device(s) with no parent: ${orphans.map(d => d.device_name).join(', ')}`,
        };
        return { status: 'pass', severity: 'pass', details: 'All devices linked to parents' };
      },
    },

    // ─── Data Quality ───────────────────────────────────────────────────
    {
      id: 'screenshots-storage',
      category: 'Data Quality',
      name: 'Screenshot storage accessible',
      run: async () => {
        const { data: ss } = await supabase.from('screenshots').select('image_url').limit(1);
        if (!ss || ss.length === 0) return { status: 'pass', severity: 'pass', details: 'No screenshots yet' };
        const { data: signed, error } = await supabase.storage
          .from('parentguard')
          .createSignedUrl(ss[0].image_url, 60);
        if (error) return {
          status: 'fail', severity: 'high',
          details: `Storage bucket error: ${error.message}`,
        };
        return { status: 'pass', severity: 'pass', details: 'Storage bucket "parentguard" accessible and signing works' };
      },
    },
    {
      id: 'alerts-unread-count',
      category: 'Data Quality',
      name: 'Unread alerts check',
      run: async () => {
        const { count } = await supabase
          .from('alerts').select('*', { count: 'exact', head: true })
          .eq('is_read', false);
        const total = count || 0;
        if (total > 100) return {
          status: 'warn', severity: 'medium',
          details: `${total} unread alerts — parents may be ignoring alerts, consider email notifications`,
        };
        return { status: 'pass', severity: 'pass', details: `${total} unread alert(s)` };
      },
    },
    {
      id: 'data-today-activity',
      category: 'Data Quality',
      name: 'Recent data flowing (last 24h)',
      run: async () => {
        const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const [
          { count: appCount },
          { count: siteCount },
          { count: ssCount },
        ] = await Promise.all([
          supabase.from('app_usage').select('*', { count: 'exact', head: true }).gte('timestamp', yesterday),
          supabase.from('websites').select('*', { count: 'exact', head: true }).gte('timestamp', yesterday),
          supabase.from('screenshots').select('*', { count: 'exact', head: true }).gte('timestamp', yesterday),
        ]);
        const hasData = (appCount || 0) + (siteCount || 0) + (ssCount || 0) > 0;
        if (!hasData) return {
          status: 'warn', severity: 'medium',
          details: 'No app usage, website visits, or screenshots in the last 24 hours. Are any agents running?',
        };
        return {
          status: 'pass', severity: 'pass',
          details: `Last 24h: ${appCount || 0} app events, ${siteCount || 0} site visits, ${ssCount || 0} screenshots`,
        };
      },
    },

    // ─── Security Checks ────────────────────────────────────────────────
    {
      id: 'sec-kids-column',
      category: 'Security',
      name: 'Kids table uses customer_id (not parent_id)',
      run: async () => {
        const { data, error } = await supabase.from('kids').select('customer_id').limit(1);
        if (error && error.message.includes('customer_id')) return {
          status: 'fail', severity: 'critical',
          details: 'kids table does not have customer_id column — data inserts will fail',
        };
        return { status: 'pass', severity: 'pass', details: 'kids.customer_id column exists and accessible' };
      },
    },
    {
      id: 'sec-no-empty-passwords',
      category: 'Security',
      name: 'No customers with empty auth_id',
      run: async () => {
        const { data } = await supabase.from('customers').select('id, name, email').is('auth_id', null);
        if ((data || []).length > 0) return {
          status: 'warn', severity: 'medium',
          details: `${data.length} customer(s) with NULL auth_id: ${data.map(c => c.email || c.name).join(', ')}`,
        };
        return { status: 'pass', severity: 'pass', details: 'All customers have auth_id' };
      },
    },
  ];
}

/* ═══════════════════════════════════════════════════════════════════════════
   Recommendations Engine
   ═══════════════════════════════════════════════════════════════════════════ */

function getRecommendations(results, dbStats) {
  const recs = [];

  // Based on test results
  const failures = results.filter(r => r.status === 'fail');
  const warnings = results.filter(r => r.status === 'warn');

  if (failures.length > 0) {
    recs.push({
      priority: 'critical',
      title: `${failures.length} failing test(s) need immediate attention`,
      details: failures.map(f => `- ${f.name}: ${f.details}`).join('\n'),
    });
  }

  // Business recommendations
  if (dbStats.trialToFree > 0) {
    recs.push({
      priority: 'high',
      title: `${dbStats.trialToFree} expired trial(s) — send win-back emails`,
      details: 'These users tried the product but did not convert. Consider a limited-time discount offer.',
    });
  }

  if (dbStats.totalCustomers > 0 && dbStats.paidCustomers === 0) {
    recs.push({
      priority: 'high',
      title: 'No paying customers yet',
      details: 'Focus on converting trial users. Consider adding in-app upgrade prompts and feature gating.',
    });
  }

  if (dbStats.kidsWithoutDevices > 0) {
    recs.push({
      priority: 'medium',
      title: `${dbStats.kidsWithoutDevices} kid(s) without connected devices`,
      details: 'These parents signed up but haven\'t installed the agent. Send setup reminder emails.',
    });
  }

  if (warnings.length > 0) {
    recs.push({
      priority: 'low',
      title: `${warnings.length} warning(s) to review`,
      details: warnings.map(w => `- ${w.name}`).join('\n'),
    });
  }

  // UX recommendations (always show)
  recs.push({
    priority: 'low',
    title: 'Add onboarding checklist for new users',
    details: 'Competitors like Bark use a setup checklist (add child, install agent, configure alerts). Reduces drop-off by 40%.',
  });

  return recs;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════════════════ */

export default function QATesting() {
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTest, setCurrentTest] = useState('');
  const [lastRun, setLastRun] = useState(null);
  const [dbStats, setDbStats] = useState({
    totalCustomers: 0, paidCustomers: 0, trialToFree: 0, kidsWithoutDevices: 0,
  });
  const [activeTab, setActiveTab] = useState('results'); // results | recommendations | history

  const tests = getTests(supabase);

  const runAllTests = useCallback(async () => {
    setRunning(true);
    setResults([]);
    setProgress(0);
    const newResults = [];

    for (let i = 0; i < tests.length; i++) {
      const test = tests[i];
      setCurrentTest(test.name);
      setProgress(Math.round(((i) / tests.length) * 100));

      try {
        const result = await test.run();
        newResults.push({
          id: test.id,
          category: test.category,
          name: test.name,
          ...result,
          timestamp: new Date().toLocaleTimeString(),
        });
      } catch (err) {
        newResults.push({
          id: test.id,
          category: test.category,
          name: test.name,
          status: 'fail',
          severity: 'critical',
          details: `Test crashed: ${err.message}`,
          timestamp: new Date().toLocaleTimeString(),
        });
      }
      setResults([...newResults]);
    }

    setProgress(100);
    setCurrentTest('');
    setRunning(false);
    setLastRun(new Date());

    // Gather DB stats for recommendations
    try {
      const { data: custs } = await supabase.from('customers').select('id, plan, trial_ends_at');
      const { data: allKids } = await supabase.from('kids').select('id, customer_id');
      const { data: devs } = await supabase.from('devices').select('parent_id');

      const now = new Date();
      const parentIdsWithDevices = new Set((devs || []).map(d => d.parent_id));
      const custIds = (custs || []).map(c => c.id);
      const kidsCustomerIds = new Set((allKids || []).map(k => k.customer_id));

      setDbStats({
        totalCustomers: (custs || []).length,
        paidCustomers: (custs || []).filter(c => c.plan === 'plus' || c.plan === 'family').length,
        trialToFree: (custs || []).filter(c => c.plan === 'trial' && c.trial_ends_at && new Date(c.trial_ends_at) < now).length,
        kidsWithoutDevices: custIds.filter(id => kidsCustomerIds.has(id) && !parentIdsWithDevices.has(id)).length,
      });
    } catch (e) { /* ignore */ }

    // Save run to localStorage history
    const history = JSON.parse(localStorage.getItem('qa_history') || '[]');
    const passCount = newResults.filter(r => r.status === 'pass').length;
    const failCount = newResults.filter(r => r.status === 'fail').length;
    const warnCount = newResults.filter(r => r.status === 'warn').length;
    history.unshift({
      date: new Date().toISOString(),
      total: newResults.length,
      pass: passCount,
      fail: failCount,
      warn: warnCount,
      score: Math.round((passCount / newResults.length) * 100),
    });
    localStorage.setItem('qa_history', JSON.stringify(history.slice(0, 20)));
  }, [tests]);

  // Load history
  const history = JSON.parse(localStorage.getItem('qa_history') || '[]');

  // Categorize results
  const categories = {};
  results.forEach(r => {
    if (!categories[r.category]) categories[r.category] = [];
    categories[r.category].push(r);
  });

  const passCount = results.filter(r => r.status === 'pass').length;
  const failCount = results.filter(r => r.status === 'fail').length;
  const warnCount = results.filter(r => r.status === 'warn').length;
  const score = results.length > 0 ? Math.round((passCount / results.length) * 100) : null;

  const recommendations = results.length > 0 ? getRecommendations(results, dbStats) : [];

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 24, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h1 className="page-title">QA Testing & Health Monitor</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            Automated testing, bug detection, and recommendations for ParentsProtection
          </p>
        </div>
        <button
          className="btn"
          onClick={runAllTests}
          disabled={running}
          style={{
            padding: '12px 28px', fontSize: 14, fontWeight: 700,
            background: running ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
            border: 'none', borderRadius: 10, cursor: running ? 'not-allowed' : 'pointer',
          }}
        >
          {running ? '\u23F3 Running Tests...' : '\u25B6\uFE0F Run All Tests'}
        </button>
      </div>

      {/* Progress bar while running */}
      {running && (
        <div style={{ marginBottom: 20 }}>
          <ProgressBar progress={progress} label={currentTest || 'Starting...'} />
        </div>
      )}

      {/* Score Card */}
      {score !== null && !running && (
        <div style={{
          background: score >= 80
            ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
            : score >= 50
              ? 'linear-gradient(135deg, #fefce8, #fef9c3)'
              : 'linear-gradient(135deg, #fef2f2, #fee2e2)',
          border: `2px solid ${score >= 80 ? '#86efac' : score >= 50 ? '#fde047' : '#fca5a5'}`,
          borderRadius: 16, padding: '24px 28px', marginBottom: 24,
          display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
        }}>
          {/* Score circle */}
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: score >= 80 ? '#22c55e' : score >= 50 ? '#eab308' : '#ef4444',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 28, fontWeight: 900, flexShrink: 0,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>
            {score}
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
              Health Score: {score >= 80 ? 'Healthy' : score >= 50 ? 'Needs Attention' : 'Critical Issues'}
            </div>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {passCount} passed, {failCount} failed, {warnCount} warnings out of {results.length} tests
            </div>
            {lastRun && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Last run: {lastRun.toLocaleString()}
              </div>
            )}
          </div>

          {/* Quick stats */}
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#22c55e' }}>{passCount}</div>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>PASSED</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#ef4444' }}>{failCount}</div>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>FAILED</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#eab308' }}>{warnCount}</div>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>WARNINGS</div>
            </div>
          </div>
        </div>
      )}

      {/* Tab bar */}
      {results.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
          {[
            { key: 'results', label: `Test Results (${results.length})`, icon: '\uD83E\uDDEA' },
            { key: 'recommendations', label: `Recommendations (${recommendations.length})`, icon: '\uD83D\uDCA1' },
            { key: 'history', label: `History (${history.length})`, icon: '\uD83D\uDCCA' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding: '10px 20px', fontSize: 13, fontWeight: 600,
              background: activeTab === tab.key ? '#0f172a' : '#f1f5f9',
              color: activeTab === tab.key ? '#fff' : '#64748b',
              border: 'none', borderRadius: 8, cursor: 'pointer',
              transition: 'all 0.15s',
            }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && results.length > 0 && (
        <div>
          {Object.entries(categories).map(([category, catResults]) => (
            <div key={category} style={{ marginBottom: 24 }}>
              <div style={{
                fontSize: 12, fontWeight: 800, color: '#64748b',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                marginBottom: 10, paddingBottom: 6,
                borderBottom: '1px solid #e2e8f0',
              }}>
                {category}
                <span style={{
                  marginLeft: 8, fontSize: 11, fontWeight: 600,
                  color: catResults.every(r => r.status === 'pass') ? '#22c55e' : '#ef4444',
                }}>
                  {catResults.filter(r => r.status === 'pass').length}/{catResults.length} passed
                </span>
              </div>
              {catResults.map(r => (
                <ResultCard key={r.id} title={r.name} status={r.status}
                  severity={r.severity} details={r.details} fix={r.fix}
                  timestamp={r.timestamp} />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Recommendations Tab */}
      {activeTab === 'recommendations' && recommendations.length > 0 && (
        <div>
          {recommendations.map((rec, i) => (
            <ResultCard key={i} title={rec.title} status={rec.priority === 'critical' ? 'fail' : 'warn'}
              severity={rec.priority} details={rec.details} />
          ))}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {history.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              No test history yet. Run your first test!
            </div>
          ) : (
            <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 11, color: '#64748b', fontWeight: 700 }}>Date</th>
                  <th style={{ textAlign: 'center', padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 11, color: '#64748b', fontWeight: 700 }}>Score</th>
                  <th style={{ textAlign: 'center', padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 11, color: '#22c55e', fontWeight: 700 }}>Pass</th>
                  <th style={{ textAlign: 'center', padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 11, color: '#ef4444', fontWeight: 700 }}>Fail</th>
                  <th style={{ textAlign: 'center', padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 11, color: '#eab308', fontWeight: 700 }}>Warn</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 16px' }}>
                      {new Date(h.date).toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 36, height: 36, borderRadius: '50%',
                        background: h.score >= 80 ? '#dcfce7' : h.score >= 50 ? '#fef9c3' : '#fee2e2',
                        color: h.score >= 80 ? '#166534' : h.score >= 50 ? '#854d0e' : '#991b1b',
                        fontSize: 12, fontWeight: 800,
                      }}>
                        {h.score}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center', color: '#22c55e', fontWeight: 700 }}>{h.pass}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center', color: '#ef4444', fontWeight: 700 }}>{h.fail}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center', color: '#eab308', fontWeight: 700 }}>{h.warn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {results.length === 0 && !running && (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          background: '#f8fafc', borderRadius: 16,
          border: '2px dashed #e2e8f0',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{'\uD83E\uDDEA'}</div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
            QA Testing Suite
          </h2>
          <p style={{ fontSize: 14, color: '#64748b', maxWidth: 440, margin: '0 auto 24px', lineHeight: 1.6 }}>
            Run automated tests to check database health, data integrity, security,
            business logic, and get smart recommendations to improve your app.
          </p>
          <div style={{ display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 28 }}>
            {[
              { icon: '\uD83D\uDDC4\uFE0F', label: 'Database Health', desc: '5 tests' },
              { icon: '\uD83D\uDCBC', label: 'Business Logic', desc: '2 tests' },
              { icon: '\uD83D\uDD17', label: 'Devices', desc: '2 tests' },
              { icon: '\uD83D\uDEE1\uFE0F', label: 'Security', desc: '2 tests' },
              { icon: '\uD83D\uDCC8', label: 'Data Quality', desc: '3 tests' },
            ].map(item => (
              <div key={item.label} style={{ textAlign: 'center', width: 100 }}>
                <div style={{ fontSize: 28, marginBottom: 4 }}>{item.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{item.label}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{item.desc}</div>
              </div>
            ))}
          </div>
          <button
            className="btn"
            onClick={runAllTests}
            style={{
              padding: '14px 36px', fontSize: 15, fontWeight: 700,
              background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
              borderRadius: 12,
            }}
          >
            {'\u25B6\uFE0F'} Run All Tests
          </button>
        </div>
      )}
    </div>
  );
}
